import { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Eye, Download, FileText, CheckCircle2, Circle, ScrollText, AlertTriangle, Lock } from "lucide-react";
import { AppLayout } from "@/components/AppLayout";
import { StatusPill, PositionChip } from "@/components/StatusPill";
import { FileDrop } from "@/components/FileDrop";
import { viewProtected, downloadProtected } from "@/lib/files";
import api, { API, apiError } from "@/lib/api";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");
const fmtAmount = (a) => {
  if (a == null) return "—";
  const hasCents = Math.round(a * 100) % 100 !== 0;
  return new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", minimumFractionDigits: hasCents ? 2 : 0, maximumFractionDigits: 2, useGrouping: "always" }).format(a);
};

const TIMELINE = [
  { key: "recibido", t: "Recibido" },
  { key: "en_analisis", t: "En análisis" },
  { key: "terminado", t: "Terminado" },
];

function Metric({ label, value }) {
  return (
    <div className="ddp-card" style={{ padding: "16px 16px 20px", background: "var(--paper-2)" }}>
      <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: ".1em", color: "var(--muted)", fontWeight: 700, marginBottom: 12 }}>{label}</div>
      <div className="ddp-serif" style={{ fontSize: 22, color: "var(--burgundy-dark)", lineHeight: 1.1 }}>{value || "—"}</div>
    </div>
  );
}

export default function AsuntoDetalle() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const [c, setC] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const isNew = sp.get("nuevo") === "1";

  const load = useCallback(() => {
    api.get(`/cases/${id}`).then(({ data }) => setC(data)).catch(() => setNotFound(true));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (notFound) {
    return (
      <AppLayout variant="firm">
        <div className="ddp-card" style={{ padding: 40, textAlign: "center" }}>
          <div className="ddp-serif" style={{ fontSize: 22, marginBottom: 8 }}>Asunto no disponible</div>
          <Link to="/asuntos" className="ddp-btn ddp-btn-ghost ddp-btn-sm" style={{ display: "inline-flex" }}>Volver a mis asuntos</Link>
        </div>
      </AppLayout>
    );
  }
  if (!c) return <AppLayout variant="firm"><div style={{ padding: 40, color: "var(--muted)" }}>Cargando…</div></AppLayout>;

  const stepIndex = TIMELINE.findIndex((s) => s.key === c.status);
  const terminado = c.status === "terminado";
  const r = c.result || {};

  const openPdf = async () => {
    try { await viewProtected(`/cases/${id}/result/pdf`); }
    catch (e) { toast.error(apiError(e, "No se pudo abrir la Hoja de Pronóstico.")); }
  };
  const downloadPdf = async () => {
    try { await downloadProtected(`/cases/${id}/result/pdf`, `Hoja_DDP_${c.reference}.pdf`); }
    catch (e) { toast.error(apiError(e, "No se pudo descargar la Hoja de Pronóstico.")); }
  };

  return (
    <AppLayout variant="firm">
      <Link to="/asuntos" data-testid="back-link" style={{ display: "inline-flex", alignItems: "center", gap: 7, color: "var(--muted)", textDecoration: "none", fontSize: 14, fontWeight: 600 }}>
        <ArrowLeft size={16} /> Volver a mis asuntos
      </Link>

      {isNew && !terminado && (
        <div data-testid="received-confirmation" style={{ marginTop: 18, background: "#EBF2E9", border: "1px solid #CBDCC9", borderRadius: 8, padding: "18px 20px", display: "flex", gap: 12, alignItems: "flex-start" }}>
          <CheckCircle2 size={22} style={{ color: "var(--green)", flexShrink: 0 }} />
          <div>
            <div className="ddp-serif" style={{ fontSize: 19, color: "#2f5a36" }}>Expediente recibido.</div>
            <div style={{ fontSize: 14, color: "#3c5a41", marginTop: 3 }}>Su asunto ha sido registrado correctamente y será sometido al análisis DDP.</div>
          </div>
        </div>
      )}

      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap", marginTop: 20 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <span className="ddp-ref" style={{ fontSize: 15 }}>{c.reference}</span>
            <StatusPill status={c.status_display} />
            <span style={{ fontSize: 12, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".06em" }}>{c.type === "demo" ? "Caso cerrado" : "Asunto en curso"}</span>
          </div>
          <h1 className="ddp-serif" style={{ fontSize: "clamp(26px,3.6vw,38px)", color: "var(--burgundy-dark)", margin: "10px 0 0", letterSpacing: "-.02em" }}>
            {terminado ? "Análisis completado" : c.title}
          </h1>
          {terminado && <div style={{ color: "var(--muted)", marginTop: 6, fontSize: 15 }}>{c.title}</div>}
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) 300px", gap: 28, marginTop: 26, alignItems: "start" }} className="na-grid">
        <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
          {terminado ? (
            <>
              {/* RESULT */}
              <div className="ddp-card" style={{ padding: 26 }}>
                {r.position && <div style={{ marginBottom: 16 }}><PositionChip position={r.position} /></div>}
                {r.recommended_decision && (
                  <>
                    <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: ".06em", textTransform: "uppercase", color: "var(--muted)", marginBottom: 8 }}>Decisión recomendada</div>
                    <div className="ddp-serif" style={{ fontSize: 24, lineHeight: 1.25, color: "var(--ink)" }}>{r.recommended_decision}</div>
                  </>
                )}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(130px,1fr))", gap: 12, marginTop: 22 }}>
                  <Metric label="Reclamado" value={r.claimed_amount} />
                  <Metric label="Resultado central" value={r.central_result} />
                  <Metric label="Banda útil" value={r.useful_band} />
                  <Metric label="Riesgo principal" value={r.main_risk} />
                </div>
              </div>

              {r.observations && (
                <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 22 }} className="demo-cols">
                  <div className="ddp-card" style={{ padding: 24 }}>
                    <div className="ddp-serif" style={{ fontSize: 18, color: "var(--burgundy-dark)", marginBottom: 10 }}>Observaciones clave</div>
                    <p style={{ fontSize: 14.5, lineHeight: 1.6, color: "#3D3834", margin: 0, whiteSpace: "pre-wrap" }}>{r.observations}</p>
                  </div>
                </div>
              )}

              {/* Hoja de Pronóstico */}
              <div className="ddp-card" style={{ padding: 24, display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
                <div style={{ background: "var(--burgundy)", color: "#fff", borderRadius: 8, padding: 12 }}><ScrollText size={22} /></div>
                <div style={{ flex: 1, minWidth: 180 }}>
                  <div className="ddp-serif" style={{ fontSize: 18, color: "var(--ink)" }}>Hoja de Pronóstico</div>
                  <div style={{ fontSize: 13, color: "var(--muted)" }}>Documento DDP · una página</div>
                </div>
                {r.has_pdf ? (
                  <div style={{ display: "flex", gap: 10 }}>
                    <button onClick={openPdf} data-testid="view-sheet" className="ddp-btn ddp-btn-secondary ddp-btn-sm"><Eye size={15} /> Ver hoja</button>
                    <button onClick={downloadPdf} data-testid="download-sheet" className="ddp-btn ddp-btn-primary ddp-btn-sm"><Download size={15} /> Descargar PDF</button>
                  </div>
                ) : (
                  <span style={{ fontSize: 13, color: "var(--muted)" }}>PDF no disponible</span>
                )}
              </div>

              <RealOutcome caseData={c} onDone={load} />
            </>
          ) : (
            <>
              {/* Case data while pending */}
              <div className="ddp-card" style={{ padding: 26 }}>
                <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: ".06em", textTransform: "uppercase", color: "var(--muted)", marginBottom: 18 }}>Datos del asunto</div>
                <dl style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: "18px 24px", margin: 0 }}>
                  <Field label="Parte representada" value={c.represented_party === "actora" ? "Actora" : "Demandada"} />
                  <Field label="Cuantía" value={fmtAmount(c.amount)} />
                  <Field label="Fecha" value={fmtDate(c.created_at)} />
                  <Field label="Tipo" value={c.type === "demo" ? "Caso cerrado" : "Asunto en curso"} />
                </dl>
                <div className="ddp-hr" style={{ margin: "20px 0" }} />
                <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: ".06em", textTransform: "uppercase", color: "var(--muted)", marginBottom: 8 }}>Problema planteado</div>
                <p style={{ fontSize: 15, lineHeight: 1.6, color: "#3D3834", margin: 0, whiteSpace: "pre-wrap" }}>{c.question}</p>
              </div>

              <Documents files={c.files} caseId={id} />

              <PrepPanel c={c} caseId={id} reload={load} />

              {c.type === "demo" && (
                <div style={{ display: "flex", gap: 12, alignItems: "flex-start", background: "#F3E7E8", border: "1px solid #E3CBCF", borderRadius: 7, padding: "16px 18px" }}>
                  <AlertTriangle size={20} style={{ color: "var(--burgundy)", flexShrink: 0, marginTop: 1 }} />
                  <div style={{ fontSize: 13.5, color: "var(--burgundy-dark)", lineHeight: 1.55, fontWeight: 600 }}>
                    NO aporte la sentencia ni la resolución final antes de recibir el análisis DDP.
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Sidebar timeline + docs */}
        <aside style={{ display: "flex", flexDirection: "column", gap: 20, position: "sticky", top: 24 }}>
          <div className="ddp-card" style={{ padding: 24 }}>
            <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: ".06em", textTransform: "uppercase", color: "var(--muted)", marginBottom: 18 }}>Estado del análisis</div>
            {TIMELINE.map((s, i, arr) => {
              const done = i <= stepIndex;
              return (
                <div key={s.key} style={{ display: "flex", gap: 12 }}>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                    {done ? <CheckCircle2 size={20} style={{ color: "var(--burgundy)" }} /> : <Circle size={20} style={{ color: "var(--line)" }} />}
                    {i < arr.length - 1 && <div style={{ width: 1.5, flex: 1, minHeight: 22, background: i < stepIndex ? "var(--burgundy)" : "var(--line)", margin: "4px 0" }} />}
                  </div>
                  <div style={{ paddingBottom: 12 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: done ? "var(--burgundy-dark)" : "var(--ink)" }}>{s.t}</div>
                    <div style={{ fontSize: 12, color: "var(--muted)" }}>
                      {s.key === "recibido" && fmtDate(c.created_at)}
                      {s.key === "en_analisis" && (c.started_at ? fmtDate(c.started_at) : "Pendiente")}
                      {s.key === "terminado" && (c.completed_at ? fmtDate(c.completed_at) : "Pendiente")}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {terminado && <Documents files={c.files} caseId={id} compact />}
        </aside>
      </div>
    </AppLayout>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <dt style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".08em", color: "var(--muted)", fontWeight: 700, marginBottom: 5 }}>{label}</dt>
      <dd style={{ margin: 0, fontSize: 15, color: "var(--ink)" }}>{value}</dd>
    </div>
  );
}

const DOC_LABEL = { demanda: "Demanda", contestacion: "Contestación", adicional: "Documentación adicional", sentencia: "Sentencia / resolución", prepared: "Versión preparada por DDP" };

const INCIDENT_OPTS = [
  ["identificador_personal", "Permanece un identificador personal."],
  ["informacion_relevante_oculta", "Se ha ocultado información relevante."],
  ["documento_no_visualiza", "El documento no se visualiza correctamente."],
  ["falta_pagina_archivo", "Falta una página o un archivo."],
  ["otra", "Otra incidencia concreta."],
];

function PrepPanel({ c, caseId, reload }) {
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState(null); // null | 'incident'
  const [cat, setCat] = useState("identificador_personal");
  const [note, setNote] = useState("");
  const ps = c.preparation_status;

  const reconfirm = async () => {
    setBusy(true);
    try { await api.post(`/cases/${caseId}/reconfirm`, { firm_confirmation: true }); toast.success("Corrección reenviada."); reload(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const approve = async () => {
    setBusy(true);
    try { await api.post(`/cases/${caseId}/prepared/approve`); toast.success("Versión preparada aprobada. Puede iniciarse el análisis."); reload(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const report = async () => {
    setBusy(true);
    try { await api.post(`/cases/${caseId}/prepared/incident`, { category: cat, note }); toast.success("Incidencia comunicada."); setMode(null); reload(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  if (ps === "returned_for_correction") {
    return (
      <div className="ddp-card" data-testid="returned-banner" style={{ padding: 22, border: "1px solid #E3CBCF", background: "#F3E7E8" }}>
        <div className="ddp-serif" style={{ fontSize: 18, color: "var(--burgundy-dark)", marginBottom: 6 }}>Documentación devuelta para corrección</div>
        <p style={{ fontSize: 13.5, color: "#5a3b40", margin: "0 0 12px", lineHeight: 1.55 }}><strong>Motivo:</strong> {c.returned_reason || "—"}</p>
        <p style={{ fontSize: 13, color: "var(--muted)", margin: "0 0 14px" }}>Añada la documentación corregida arriba y vuelva a confirmarla.</p>
        <button onClick={reconfirm} disabled={busy} data-testid="reconfirm-btn" className="ddp-btn ddp-btn-primary ddp-btn-sm">Confirmar corrección y reenviar</button>
      </div>
    );
  }
  if (c.preparation_mode === "ddp" && ps === "prepared_ready_review") {
    return (
      <div className="ddp-card" data-testid="prepared-review" style={{ padding: 24 }}>
        <div className="ddp-serif" style={{ fontSize: 18, color: "var(--burgundy-dark)", marginBottom: 6 }}>Versión preparada por DDP</div>
        <p style={{ fontSize: 13.5, color: "var(--muted)", margin: "0 0 16px", lineHeight: 1.55 }}>Revise la versión preparada (arriba, en Documentación) y apruebe para autorizar el análisis, o comunique una incidencia concreta.</p>
        {mode === "incident" ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div>
              <label className="ddp-label">Categoría</label>
              <select className="ddp-input" data-testid="incident-cat" value={cat} onChange={(e) => setCat(e.target.value)}>
                {INCIDENT_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className="ddp-label">Detalle (documento, página o pasaje) <span style={{ color: "var(--muted)", fontWeight: 400 }}>(opcional)</span></label>
              <textarea className="ddp-textarea" data-testid="incident-note" value={note} onChange={(e) => setNote(e.target.value)} style={{ minHeight: 80 }} />
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={report} disabled={busy} data-testid="incident-submit" className="ddp-btn ddp-btn-primary ddp-btn-sm">Comunicar incidencia</button>
              <button onClick={() => setMode(null)} className="ddp-btn ddp-btn-ghost ddp-btn-sm">Cancelar</button>
            </div>
          </div>
        ) : (
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button onClick={approve} disabled={busy} data-testid="approve-prepared" className="ddp-btn ddp-btn-primary ddp-btn-sm">Aprobar y autorizar el análisis</button>
            <button onClick={() => setMode("incident")} data-testid="report-incident" className="ddp-btn ddp-btn-secondary ddp-btn-sm">Comunicar una incidencia</button>
          </div>
        )}
      </div>
    );
  }
  if (c.preparation_mode === "ddp" && (ps === "awaiting_ddp_preparation" || ps === "incident_open")) {
    return (
      <div className="ddp-card" data-testid="prep-waiting" style={{ padding: 20, background: "var(--paper-2)" }}>
        <div style={{ fontSize: 13.5, color: "var(--muted)", lineHeight: 1.55 }}>
          {ps === "incident_open" ? "Se ha comunicado una incidencia. DDP preparará una versión corregida para su revisión." : "DDP está preparando una versión minimizada de la documentación. Podrá revisarla y aprobarla antes del análisis."}
        </div>
      </div>
    );
  }
  return null;
}

function Documents({ files = [], caseId, compact = false }) {
  const visible = files.filter((f) => f.type !== "sentencia");
  const dl = async (f) => {
    try { await downloadProtected(`/cases/${caseId}/files/${f.id}/download`, f.filename); }
    catch (e) { toast.error("No se pudo descargar el documento."); }
  };
  return (
    <div className="ddp-card" style={{ padding: compact ? 20 : 24 }}>
      <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: ".06em", textTransform: "uppercase", color: "var(--muted)", marginBottom: 14 }}>Documentación</div>
      {visible.length === 0 ? (
        <div style={{ fontSize: 13.5, color: "var(--muted)" }}>Sin documentos adjuntos.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {visible.map((f) => (
            <button key={f.id} onClick={() => dl(f)} data-testid={`file-${f.id}`} style={{ display: "flex", alignItems: "center", gap: 10, background: "var(--paper-2)", border: "1px solid var(--line)", borderRadius: 5, padding: "10px 12px", cursor: "pointer", textAlign: "left" }}>
              <FileText size={16} style={{ color: "var(--burgundy)", flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.filename}</div>
                <div style={{ fontSize: 11, color: "var(--muted)" }}>{DOC_LABEL[f.type] || f.type}</div>
              </div>
              <Download size={15} style={{ color: "var(--muted)" }} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function RealOutcome({ caseData, onDone }) {
  const existing = caseData.real_outcome;
  const [open, setOpen] = useState(false);
  const [outcome, setOutcome] = useState("");
  const [awarded, setAwarded] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!outcome.trim()) return toast.error("Indique el resultado real.");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("outcome", outcome);
      fd.append("awarded_amount", awarded);
      if (file) fd.append("file", file);
      await api.post(`/cases/${caseData.id}/real-outcome`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Resultado real registrado.");
      setOpen(false);
      onDone();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ddp-card" style={{ padding: 24, borderStyle: "dashed" }}>
      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 6 }}>
        <Lock size={16} style={{ color: "var(--burgundy)" }} />
        <div className="ddp-serif" style={{ fontSize: 18, color: "var(--burgundy-dark)" }}>Contrastar resultado real</div>
      </div>
      <p style={{ fontSize: 13.5, color: "var(--muted)", margin: "0 0 16px", lineHeight: 1.55 }}>
        El análisis DDP ya ha sido cerrado y no puede modificarse. Si lo desea, puede aportar ahora el desenlace real del procedimiento para su contraste.
      </p>

      {existing ? (
        <div data-testid="real-outcome-registered" style={{ background: "var(--paper-2)", border: "1px solid var(--line)", borderRadius: 6, padding: "14px 16px" }}>
          <div style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: ".08em", color: "var(--muted)", fontWeight: 700, marginBottom: 8 }}>Resultado real registrado</div>
          <div style={{ fontSize: 14 }}><strong>Resultado:</strong> {existing.outcome}</div>
          {existing.awarded_amount && <div style={{ fontSize: 14, marginTop: 4 }}><strong>Cuantía concedida:</strong> {existing.awarded_amount}</div>}
          <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 6 }}>Registrado el {fmtDate(existing.submitted_at)}</div>
        </div>
      ) : open ? (
        <form onSubmit={submit} data-testid="real-outcome-form" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div>
            <label className="ddp-label">Resultado real</label>
            <textarea className="ddp-textarea" style={{ minHeight: 90 }} data-testid="ro-outcome" value={outcome} onChange={(e) => setOutcome(e.target.value)} placeholder="Desenlace del procedimiento" />
          </div>
          <div>
            <label className="ddp-label">Cuantía concedida <span style={{ color: "var(--muted)", fontWeight: 400 }}>(opcional)</span></label>
            <input className="ddp-input" data-testid="ro-awarded" value={awarded} onChange={(e) => setAwarded(e.target.value)} placeholder="285 €" />
          </div>
          <FileDrop label="Sentencia / resolución final" value={file} onChange={setFile} testid="ro-file" />
          <div style={{ display: "flex", gap: 10 }}>
            <button type="submit" disabled={busy} data-testid="ro-submit" className="ddp-btn ddp-btn-primary ddp-btn-sm">{busy ? "Guardando…" : "Registrar resultado"}</button>
            <button type="button" onClick={() => setOpen(false)} className="ddp-btn ddp-btn-ghost ddp-btn-sm">Cancelar</button>
          </div>
        </form>
      ) : (
        <button onClick={() => setOpen(true)} data-testid="open-real-outcome" className="ddp-btn ddp-btn-secondary ddp-btn-sm">Aportar resultado real</button>
      )}
    </div>
  );
}
